import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Shapes, Sparkles, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Sheet } from 'components/ui/sheet'
import { cn } from 'lib/utils'
import { ALL_ELEMENTS, fetchElements } from 'slices/presentationSlice'

import type { AssetName } from '../hooks/useAssetNames'
import type { BlockView } from '../hooks/useResolvedBlocks'
import { REGION_LABELS } from '../layouts'
import * as presentationService from '../services/presentationService'
import type { AssetOption, ElementDescriptor, EntityRef, PresentationScope, SlideLayout } from '../types/presentation.types'
import { sampleConfig } from '../utils/configDefaults'
import { AnchorChip } from './AnchorChip'
import { AssetPicker, SCOPE_OPTIONS } from './AssetPicker'
import { BlockContent } from './SlideRenderer'

/**
 * Choose what to put in a region (MYD-495). A right-side Sheet, per the
 * ui-design skill.
 *
 * Tabs, left to right: the story's own asset (the common case, and the only
 * one that INHERITS: the block keeps no source and follows the story if its
 * anchor changes), any other asset by type, and Generic content.
 *
 * The catalog comes from the server, already filtered to what this user may
 * place. Selecting an element previews it through `resolvePreview` with the
 * same starting config it will be placed with, so the preview is honest.
 */

type Tab = 'story' | PresentationScope | 'generic'

export interface ElementChoice {
	element: string
	config: Record<string, unknown>
	/** Null to inherit the story's anchor. */
	source: EntityRef | null
}

interface ElementPickerProps {
	open: boolean
	onClose: () => void
	region: string | null
	/** The slide's layout: elements suggested for it come first (MYD-514). */
	layout?: SlideLayout | null
	storyAnchor: EntityRef | null
	anchorName: AssetName | null
	onPick: (choice: ElementChoice) => void
}

const groupBy = (elements: ElementDescriptor[]) => {
	const groups = new Map<string, ElementDescriptor[]>()
	for (const element of elements) groups.set(element.group, [...(groups.get(element.group) ?? []), element])
	return [...groups.entries()]
}

/** Whether an element lists this layout among the ones it suits. */
export const isSuggested = (element: ElementDescriptor, layout: SlideLayout | null | undefined) =>
	Boolean(layout && element.preferredLayouts?.includes(layout))

/**
 * The picker's groups for a slide's layout (MYD-514): within each group the
 * elements suggested for the layout come first, and groups holding a
 * suggestion come before groups without. Both sorts are stable, so the
 * catalog's own order holds otherwise. Nothing is ever hidden: a suggestion
 * only moves an element up, and marks it.
 */
export const groupsForLayout = (elements: ElementDescriptor[], layout: SlideLayout | null | undefined) => {
	const rank = (element: ElementDescriptor) => (isSuggested(element, layout) ? 0 : 1)
	const groups = groupBy(elements).map(([group, members]) => [group, [...members].sort((a, b) => rank(a) - rank(b))] as const)
	const groupRank = ([, members]: (typeof groups)[number]) => (members.some(element => isSuggested(element, layout)) ? 0 : 1)
	return [...groups].sort((a, b) => groupRank(a) - groupRank(b))
}

export function ElementPicker({ open, onClose, region, layout = null, storyAnchor, anchorName, onPick }: ElementPickerProps) {
	const dispatch = useDispatch<any>()
	const catalog: ElementDescriptor[] | undefined = useSelector((state: any) => state.presentations.elementsByScope[ALL_ELEMENTS])

	const [tab, setTab] = useState<Tab>(storyAnchor ? 'story' : 'generic')
	const [asset, setAsset] = useState<AssetOption | null>(null)
	const [chosen, setChosen] = useState<ElementDescriptor | null>(null)
	const [preview, setPreview] = useState<BlockView | null>(null)

	useEffect(() => {
		if (open && !catalog) dispatch(fetchElements(undefined))
	}, [open, catalog, dispatch])

	// Each opening starts fresh, on the story's asset when there is one.
	useEffect(() => {
		if (!open) return
		setTab(storyAnchor ? 'story' : 'generic')
		setAsset(null)
		setChosen(null)
	}, [open, storyAnchor])

	const scope: PresentationScope | null = tab === 'story' ? storyAnchor?.scope ?? null : tab === 'generic' ? null : tab
	const source: EntityRef | null = tab === 'story' || tab === 'generic' ? null : asset ? { scope: asset.scope, entityId: asset.entityId } : null
	const needsAsset = tab !== 'story' && tab !== 'generic' && !asset

	const elements = useMemo(
		() => (catalog ?? []).filter(element => (scope ? element.scopes.includes(scope) : element.scopes.length === 0)),
		[catalog, scope],
	)

	// Live preview of the chosen element, with the config it will be placed with.
	const config = chosen ? sampleConfig(chosen.configSchema) : null
	useEffect(() => {
		if (!chosen) return setPreview(null)
		if (!config) return setPreview({ status: 'loading', payload: null })
		let stale = false
		setPreview({ status: 'loading', payload: null })
		presentationService
			.resolvePreview([{ id: 'preview', element: chosen.key, config, source }], tab === 'story' ? storyAnchor : null)
			.then(response => !stale && setPreview(response.blocks.preview ?? { status: 'error', payload: null, message: 'No preview.' }))
			.catch(() => !stale && setPreview({ status: 'error', payload: null, message: 'The preview could not load.' }))
		return () => {
			stale = true
		}
		// `config` is derived from `chosen`; listing it would refetch on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chosen, source?.entityId, tab, storyAnchor?.entityId])

	const tabs: Array<{ key: Tab; label: string }> = [
		...(storyAnchor ? [{ key: 'story' as Tab, label: "This story's asset" }] : []),
		...SCOPE_OPTIONS.map(option => ({ key: option.scope as Tab, label: option.label })),
		{ key: 'generic', label: 'Generic' },
	]

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[440px] sm:w-[520px]">
			<div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4">
				<div>
					<h2 className="text-base font-semibold text-gray-900">Add element</h2>
					{region && <p className="text-xs text-gray-500">To: {REGION_LABELS[region] ?? region}</p>}
				</div>
				<button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="flex flex-wrap gap-1.5 border-b border-gray-200 px-4 py-3" role="tablist">
				{tabs.map(option => (
					<button
						key={option.key}
						type="button"
						role="tab"
						aria-selected={tab === option.key}
						onClick={() => {
							setTab(option.key)
							setAsset(null)
							setChosen(null)
						}}
						className={cn(
							'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
							tab === option.key ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50',
						)}
					>
						{option.label}
					</button>
				))}
			</div>

			<div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
				{tab === 'story' && (
					<div className="flex items-center gap-2 text-xs text-gray-500">
						Draws from <AnchorChip anchor={storyAnchor} name={anchorName} /> and follows it if the story changes asset.
					</div>
				)}

				{tab !== 'story' && tab !== 'generic' && (
					<AssetPicker value={asset} onChange={setAsset} scopes={[tab]} />
				)}

				{!catalog ? (
					<p className="text-sm text-muted-foreground">Loading elements...</p>
				) : elements.length === 0 ? (
					<div className="flex flex-col items-center py-8 text-center">
						<div className="mb-3 rounded-full bg-gray-100 p-3">
							<Shapes className="h-5 w-5 text-gray-400" />
						</div>
						<p className="text-sm text-gray-500">
							{scope ? `There are no elements for ${scope}s yet. Use Generic content for now.` : 'No elements are available.'}
						</p>
					</div>
				) : (
					groupsForLayout(elements, layout).map(([group, members]) => (
						<section key={group}>
							<h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{group}</h3>
							<div className="space-y-1">
								{members.map(element => (
									<button
										key={element.key}
										type="button"
										onClick={() => setChosen(element)}
										disabled={needsAsset}
										className={cn(
											'w-full rounded-md border px-3 py-2 text-left transition-colors disabled:opacity-50',
											chosen?.key === element.key ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50',
										)}
									>
										<span className="flex items-center gap-2 text-sm font-medium text-gray-900">
											{element.label}
											{isSuggested(element, layout) && (
												<span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Suggested</span>
											)}
										</span>
										{element.description && <span className="block text-xs text-gray-500">{element.description}</span>}
									</button>
								))}
							</div>
						</section>
					))
				)}
			</div>

			{chosen && (
				<div className="shrink-0 space-y-3 border-t border-gray-200 bg-gray-50 px-4 py-4">
					<div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
						<Sparkles className="h-3.5 w-3.5" /> Preview
					</div>
					<PreviewFrame>
						{config ? (
							<BlockContent block={{ id: 'preview', element: chosen.key, region: region ?? 'main', source, config }} view={preview ?? undefined} />
						) : (
							<p className="text-[22px] text-gray-500">You will finish setting this up in the inspector once it is placed.</p>
						)}
					</PreviewFrame>
					<div className="flex justify-end gap-2">
						<Button variant="ghost" isOutline onClick={() => setChosen(null)}>
							Back
						</Button>
						<Button onClick={() => onPick({ element: chosen.key, config: config ?? {}, source })}>Add to slide</Button>
					</div>
				</div>
			)}
		</Sheet>
	)
}

/**
 * A block drawn at stage scale inside the narrow sheet: a 900px-wide logical
 * box shrunk to fit, so text sizes match the slide rather than the sheet.
 */
function PreviewFrame({ children }: { children: React.ReactNode }) {
	const scale = 0.5
	return (
		<div className="overflow-hidden rounded-md border border-gray-200 bg-white" style={{ height: 320 * scale + 16 }}>
			<div className="origin-top-left p-4" style={{ width: 900, transform: `scale(${scale})` }}>
				{children}
			</div>
		</div>
	)
}

export default ElementPicker
